/**
 * Request Verification — Institution Portal (M2).
 *
 * Institution admin/staff originate a citizen verification via the
 * existing consent state machine (`POST /api/consent/request`). Runs
 * the same backend used by every VeraLok verification. The citizen
 * decides on their own device; outcomes surface in Consent Outcomes
 * and Verification Requests without any polling on this page.
 *
 * In Demo v2 the "citizen" role is played by a real human opening
 * the Demo v2 participant view on their own phone against an
 * isolated sample identity.
 *
 * Preserved elsewhere in the ecosystem:
 *   - Wallet session-QR flow (unchanged)
 *   - Backend tenant confinement (`consent.py:_caller_institution_id`)
 *   - Lock-state refusal at request time (backend returns 409)
 *   - Citizen push notification (`consent.requested`)
 *   - Audit row + webhook fan-out on decide
 */
import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2, ShieldAlert, Lock, Copy, RefreshCw, ExternalLink } from "lucide-react";
import { DemoProvenanceChip, useIsDemoTenant } from "../DemoProvenance";

const CLAIM_CATALOG = [
  { key: "name", label: "Full name" },
  { key: "portrait", label: "Portrait" },
  { key: "id_type", label: "ID type" },
  { key: "validity", label: "Validity window" },
  { key: "verification_level", label: "Verification level" },
  { key: "age_18_plus", label: "18+ signal" },
  { key: "age_21_plus", label: "21+ signal" },
];

// Neutral fallback used only when the tenant has no industry
// classification yet (edge case). For a Demo tenant we should never
// hit this — the classification is captured at Demo intake time. In
// production, an unclassified tenant is a data-quality issue that
// the operator should escalate to their VeraLok success partner
// rather than see a stale mixed-purpose list.
const PURPOSE_TEMPLATES = [
  "Verification purpose (please describe)",
];

// Classification-aware purpose suggestions. Fetched at mount from
// GET /api/institutions/me/purposes so the operator sees purposes
// appropriate to their institution's Super Institution → Sub-category.
// Custom purposes remain accepted server-side; this is a
// suggestion surface, not a whitelist.
function useSectorPurposes() {
  const [state, setState] = React.useState({
    industry: null, sub_category: null, suggested: [], loading: true,
  });
  React.useEffect(() => {
    let alive = true;
    api.get("/institutions/me/purposes")
      .then((r) => alive && setState({ ...r.data, loading: false }))
      .catch(() => alive && setState((s) => ({ ...s, loading: false })));
    return () => { alive = false; };
  }, []);
  return state;
}

export default function RequestVerification() {
  const { user } = useAuth();
  const instId = user?.institution_id;
  const sectorCtx = useSectorPurposes();
  const [vlkId, setVlkId] = useState("");
  const [purpose, setPurpose] = useState("");
  const [claims, setClaims] = useState(["name", "id_type", "validity"]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const toggle = (k) =>
    setClaims((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  const submit = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await api.post("/consent/request", {
        vlk_id: vlkId.trim(),
        vlk_inst_id: instId,
        scope: claims,
        purpose: purpose.trim() || null,
      });
      setResult(data);
      toast.success("Request sent — citizen has been notified");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = (typeof detail === "object" && (detail.code || detail.reason_code)) ||
                   (typeof detail === "string" ? detail : "request_failed");
      setError({
        code,
        message:
          (typeof detail === "object" && detail?.message) ||
          e?.message || "Verification request failed.",
        status: e?.response?.status,
      });
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setVlkId("");
    setPurpose("");
    setClaims(["name", "id_type", "validity"]);
    setResult(null);
    setError(null);
  };

  const canSubmit = !busy && !!instId && vlkId.trim().length >= 6 && claims.length > 0 && purpose.trim().length >= 3;

  if (!instId) {
    return (
      <div className="p-6 text-sm text-muted-foreground" data-testid="request-verification-no-tenant">
        This session is not bound to an institution.
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="institution-page-request-verification">
      <DemoProvenanceChip label="DEMO V2 · Request Verification — Demo v2 interaction on isolated sample identities" />
      <header>
        <h1 className="text-xl font-semibold">Request Verification</h1>
        <p className="text-sm text-muted-foreground">
          Ask a VeraLok citizen to authorise specific identity claims.
          The citizen decides on their own device — you receive only
          what they consent to release.
        </p>
      </header>

      <DemoCitizenPicker onPick={(vlkId) => setVlkId(vlkId)} />

      <div className="border rounded-xl p-5 bg-card space-y-4" data-testid="request-verification-form">
        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold block mb-1">
            Citizen VLK_ID
          </label>
          <Input
            value={vlkId}
            onChange={(e) => setVlkId(e.target.value)}
            placeholder="e.g. 100000017"
            className="font-mono"
            data-testid="request-verification-vlk-id"
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold block mb-1">
            Legitimate purpose
          </label>
          <Textarea
            rows={2}
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            placeholder="Why do you need this verification? Shown to the citizen at consent time."
            data-testid="request-verification-purpose"
          />
          <div className="flex flex-wrap gap-1 mt-2" data-testid="request-verification-purpose-suggestions">
            {/* Classification-aware suggestions from
                GET /api/institutions/me/purposes. If the tenant has
                no classified industry, fall back to the neutral
                templates so an unclassified operator can still
                proceed. */}
            {((sectorCtx.suggested && sectorCtx.suggested.length > 0)
              ? sectorCtx.suggested
              : PURPOSE_TEMPLATES
            ).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPurpose(p)}
                className="text-[10px] uppercase tracking-widest border border-border rounded-full px-2 py-1 hover:bg-muted"
                data-testid={`request-verification-purpose-template-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {p}
              </button>
            ))}
          </div>
          {sectorCtx.industry ? (
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2" data-testid="request-verification-sector-context">
              Suggestions scoped to {sectorCtx.industry.replace(/_/g, " ")}
              {sectorCtx.sub_category ? ` · ${sectorCtx.sub_category.replace(/_/g, " ")}` : ""}
            </div>
          ) : null}
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold block mb-2">
            Requested claims
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {CLAIM_CATALOG.map((c) => (
              <label
                key={c.key}
                className="flex items-center gap-2 border border-border rounded-lg px-3 py-2 cursor-pointer hover:bg-muted"
                data-testid={`request-verification-claim-${c.key}`}
              >
                <input
                  type="checkbox"
                  checked={claims.includes(c.key)}
                  onChange={() => toggle(c.key)}
                  className="accent-primary"
                />
                <span className="text-sm">{c.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={reset} disabled={busy} data-testid="request-verification-reset">
            <RefreshCw className="w-4 h-4 mr-1" /> Reset
          </Button>
          <Button onClick={submit} disabled={!canSubmit} data-testid="request-verification-submit">
            {busy ? "Sending…" : "Send request"}
          </Button>
        </div>
      </div>

      {error && <ErrorCard error={error} />}
      {result && <ResultCard result={result} />}
    </div>
  );
}

function ResultCard({ result }) {
  const isDemo = useIsDemoTenant();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.consent_id);
      toast.success("Consent ID copied");
    } catch { /* ignore */ }
  };
  const demoCitizenUrl = `${window.location.origin}/demo-citizen/${result.consent_id}`;
  return (
    <div className="border rounded-xl overflow-hidden bg-card" data-testid="request-verification-result">
      <div className="flex items-center gap-2 px-5 py-3 bg-emerald-500/10 border-b border-emerald-500/30 text-emerald-600 dark:text-emerald-400 font-semibold">
        <CheckCircle2 className="w-5 h-5" /> Request sent
      </div>
      <div className="p-5 text-sm space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-muted-foreground">Consent ID</span>
          <span className="font-mono" data-testid="request-verification-consent-id">{result.consent_id}</span>
          <button onClick={copy} className="p-1 rounded hover:bg-muted" aria-label="Copy consent id">
            <Copy className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="text-xs text-muted-foreground">
          State: <b>{result.state}</b> · Scope: {result.scope?.join(", ") || "—"}
        </div>
        {isDemo ? (
          <div className="mt-3 border border-orange-500/40 bg-orange-500/10 rounded-lg p-3">
            <div className="text-xs uppercase tracking-widest font-bold text-orange-500 mb-1">
              Demo v2 · Complete the trial loop
            </div>
            <div className="text-xs text-muted-foreground mb-2">
              Open the Demo v2 participant view below and approve or
              deny this request. In a Lane 2 evaluation, a real human
              opens this view on their own phone.
            </div>
            <a
              href={demoCitizenUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm font-semibold text-orange-400 hover:text-orange-300"
              data-testid="request-verification-open-demo-citizen"
            >
              Open Demo v2 participant view <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        ) : (
          <div className="text-xs text-muted-foreground">
            The citizen has been notified. Outcomes will surface in
            <span className="mx-1 font-medium">Consent Outcomes</span>
            and, once verified, in <span className="mx-1 font-medium">Verification Requests</span>.
          </div>
        )}
      </div>
    </div>
  );
}

function DemoCitizenPicker({ onPick }) {
  const isDemo = useIsDemoTenant();
  const [personas, setPersonas] = React.useState([]);
  React.useEffect(() => {
    if (!isDemo) return;
    let alive = true;
    api.get("/demo/citizen/personas")
      .then((r) => alive && setPersonas(r.data?.personas || []))
      .catch(() => {});
    return () => { alive = false; };
  }, [isDemo]);
  if (!isDemo || personas.length === 0) return null;
  return (
    <div className="border border-orange-500/30 bg-orange-500/5 rounded-xl p-4"
         data-testid="demo-citizen-picker">
      <div className="text-xs uppercase tracking-widest font-bold text-orange-500 mb-2">
        Demo v2 · Sample identities
      </div>
      <div className="flex flex-wrap gap-2">
        {personas.map((p) => (
          <button
            key={p.vlk_id}
            type="button"
            onClick={() => onPick(p.vlk_id)}
            className="text-xs font-mono px-3 py-1.5 rounded-full border border-orange-500/40 text-orange-300 hover:bg-orange-500/10"
            data-testid={`demo-citizen-persona-${p.full_name?.split(' ')[0]?.toLowerCase()}`}
          >
            <span className="font-semibold">{p.full_name}</span>
            <span className="mx-1 text-orange-500/60">·</span>
            {p.vlk_id}
          </button>
        ))}
      </div>
      <div className="text-[10px] text-muted-foreground mt-2">
        Click a sample identity to auto-fill their VLK_ID. All three are DOCUMENT-tier verified in the isolated Demo v2 environment.
      </div>
    </div>
  );
}

function ErrorCard({ error }) {
  const isLocked = error.code === "vlk_id_unavailable" || error.code === "identity_locked";
  const Icon = isLocked ? Lock : ShieldAlert;
  return (
    <div
      className={`border rounded-xl overflow-hidden bg-card ${isLocked ? "border-red-500/40" : "border-amber-500/40"}`}
      data-testid="request-verification-error"
    >
      <div className={`flex items-center gap-2 px-5 py-3 font-semibold ${
          isLocked ? "bg-red-500/10 text-red-600 dark:text-red-400"
                   : "bg-amber-500/10 text-amber-700 dark:text-amber-400"
      }`}>
        <Icon className="w-5 h-5" />
        {isLocked ? "Identity unavailable" : "Request refused"}
      </div>
      <div className="p-5 text-sm">
        <div className="font-mono text-xs" data-testid="request-verification-error-code">
          {error.code}{error.status ? ` · HTTP ${error.status}` : ""}
        </div>
        <div className="text-muted-foreground mt-1" data-testid="request-verification-error-message">
          {error.message}
        </div>
      </div>
    </div>
  );
}
